import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { URLParameters } from '../types';
import { continueWithCurrentSession, doLogout, manageSession } from '../actions/session-actions';
import { useNavigateToLoginPrompt } from '../utilities/hooks/history';
import {
  EventAttributes,
  useAnalyticsClient,
  useLinkClickedEvent,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import queryString from 'query-string';
import { LoginHintType, useLoginHint } from '../utilities/hooks/login-hint';
import UserAvatarCircleIcon from '@atlaskit/icon/glyph/user-avatar-circle';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import Account, {
  AccountContainer,
  AvatarContainer,
  Paragraph,
  RowButtonContainer,
  TextContainer,
} from '../components/Account';
import sessionsSelector, { Session } from '../selectors/sessions';
import { applicationDestinationSelector } from '../selectors/application-destination';
import ButtonLink from '../components/ButtonLink';
import Accounts from '../components/Accounts';
import UnauthorizedComponent from '../components/UnauthorizedComponent';
import WhiteboxPage from '../components/WhiteboxPage';
import TallButton from '../components/TallButton';
import ButtonContainer from '../components/ButtonContainer';
import { isMobileOidc } from '../utilities/oidc/oidc';
import { RequestAccessLink } from '../components/links';
import SectionMessage from '@atlaskit/section-message';
import styled from '@emotion/styled';
import { gridSize } from '@atlaskit/theme';
import { useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { usePageStateLoader, fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../utilities/feature-flags/feature-gate';
import Heading from '@atlaskit/heading';

const MAXIMUM_ASSOCIATED_SESSIONS = 9;

const ButtonWrapper = styled.div`
  padding-bottom: 0;
`;

const SectionMessageWrapper = styled.div`
  margin-top: ${gridSize() * 2}px;
`;

const UnauthorizedWrapper = styled.div`
  padding-top: ${gridSize() * 2}px;
`;

const UnauthorizedHeader = styled.div`
  padding-bottom: ${gridSize() * 2}px;
`;

const AccountsList = styled.ul`
  margin-top: 0px;
  padding: 0px;
  list-style-type: none;
`;

const AccountsListItem = styled.li`
  margin-top: 0px;
`;

const pageId = 'selectAccountScreen';

const generateDestUrl = parsedUrl => {
  let destUrl = '';
  if (parsedUrl.pathname) {
    destUrl += parsedUrl.pathname;
  }
  if (parsedUrl.search) {
    destUrl += parsedUrl.search;
  }
  return destUrl;
};

export async function selectAccountLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/login/select-account/state', request, {
    capabilities: ['capability-group--login', 'capability-name--select-account'],
  });
}

/**
 * TODO: Refactor component to separate the UI into a 'dumb' component and then separating the logic, data fetching,
 * bootstrap into a separate 'smart' component.
 * http://go/j/KIRBY-6917
 */
export const SelectAccountPage = () => {
  usePageStateLoader();

  const navigate = useNavigateSoftOrHard();
  const { search } = useLocation();
  const analyticsClient = useAnalyticsClient();
  const loginHint = useLoginHint();

  const sessions = useAppSelector(state => sessionsSelector(state, search, loginHint));

  const applicationDestination = useAppSelector(state =>
    applicationDestinationSelector(state, search)
  );

  const hideLogout = useAppSelector(
    state => isMobileOidc(state.microbranding.oidcContext) || state.microbranding.isEmbedded
  );

  const isLoading = useAppSelector(state => state.selectAccount.isLoading);

  const dispatch = useAppDispatch();

  const shouldShowRequestAccessLink = useCallback(() => {
    const params: URLParameters = queryString.parse(search);
    let parsedUrl = '';
    try {
      parsedUrl = generateDestUrl(new URL(decodeURIComponent(params.continue || '')));
    } catch (error) {
      // Do nothing
    }
    return (
      params.showRequestAccessLink === 'true' && applicationDestination !== null && parsedUrl !== ''
    );
  }, [applicationDestination, search]);

  const eventAttributes = useMemo(
    () => ({
      accountCount: sessions.length,
      loginHintType: loginHint ? loginHint.type : undefined,
      hideLogout: hideLogout,
      requestAccessLinkShown: shouldShowRequestAccessLink(),
      suggestedAccountShown: sessions.some(session => session.suggested),
    }),
    [hideLogout, loginHint, sessions, shouldShowRequestAccessLink]
  );

  usePageViewedEvent(pageId, {
    ...eventAttributes,
    noAccess: !!loginHint && loginHint.type === LoginHintType.NotEmailAddress,
    accounts: sessions.map(session => session.accountId),
  });

  const renderHeader = () => {
    const suggestedAccount = getSuggestedAccount();

    if (sessions.length === 1 && sessions[0].hint === LoginHintType.NotEmailAddress) {
      return (
        <Heading size="small" as="h1">
          <FormattedMessage
            id="try.another.account.title"
            defaultMessage="Try a different account"
          />
        </Heading>
      );
    } else if (suggestedAccount) {
      return (
        <Heading size="small" as="h1">
          <FormattedMessage id="select.account.switch.title" defaultMessage="Switch account" />
        </Heading>
      );
    } else {
      return (
        <Heading size="small" as="h1">
          <FormattedMessage
            id="select.account.title"
            defaultMessage="Choose or add another account"
          />
        </Heading>
      );
    }
  };

  const handleClickRequestAccessLink = useLinkClickedEvent(pageId, 'requestAccessLink');

  const renderMainContentFooter = () => {
    const params: URLParameters = queryString.parse(search);
    let destUrl = '';
    try {
      destUrl = generateDestUrl(new URL(decodeURIComponent(params.continue || '')));
    } catch (error) {
      //
    }
    const requestAccessLink = shouldShowRequestAccessLink() && applicationDestination && (
      <RequestAccessLink
        onClick={handleClickRequestAccessLink}
        destUrl={destUrl}
        applicationDestination={applicationDestination}
      />
    );
    if (hideLogout) {
      return <></>;
    }
    if (requestAccessLink) {
      return requestAccessLink;
    } else {
      if (sessions.length <= 1) {
        return (
          <ButtonLink
            id="redirect-to-logout"
            onClick={async () => {
              await analyticsClient.buttonClickedEvent(
                pageId,
                'forgetAllAccounts',
                eventAttributes
              );
              await dispatch(doLogout());
            }}>
            <FormattedMessage
              id="select.account.footer.redirect.to.logout"
              defaultMessage="Log out"
            />
          </ButtonLink>
        );
      }
    }
  };

  const handleSessionClick = useCallback(
    (
      session: Session,
      eventAttributes: EventAttributes
    ): ((_: React.MouseEvent<HTMLElement>) => void) | null => {
      return () => {
        if (session.current) {
          (async () => {
            await analyticsClient.buttonClickedEvent(
              pageId,
              'continueWithCurrentAccount',
              eventAttributes
            );
            await dispatch(continueWithCurrentSession(navigate));
          })();
        } else {
          analyticsClient.buttonClickedEvent(pageId, 'switchToAccount', eventAttributes);

          const request = { sessionId: session.sessionId, command: 'switch' } as const;
          dispatch(manageSession(request, navigate));
        }
      };
    },
    [analyticsClient, dispatch, navigate]
  );

  const navigateToLogin = useNavigateToLoginPrompt();

  const navigateToLoginPrompt = useCallback((): void => {
    analyticsClient.buttonClickedEvent(pageId, 'addAccount', eventAttributes);
    navigateToLogin();
  }, [analyticsClient, navigateToLogin, eventAttributes]);

  const navigateToRemoveAccountPrompt = useCallback((): void => {
    analyticsClient.buttonClickedEvent(pageId, 'removeAccountFromList', eventAttributes);
    navigate({ pathname: '/login/remove-account', search });
  }, [analyticsClient, eventAttributes, navigate, search]);

  interface LoginHintNotEmailAddressProps {
    session: Session;
    index: number;
    suggestedAccount: Session | undefined;
  }

  const LoginHintNotEmailAddress = ({
    index,
    suggestedAccount,
    session,
  }: LoginHintNotEmailAddressProps) => {
    return suggestedAccount ? (
      <Account
        id={`account-${suggestedAccount.accountId}`}
        key={`session-${suggestedAccount.sessionId}`}
        primary={true}
        avatarUrl={suggestedAccount.avatarUrl}
        displayName={suggestedAccount.displayName}
        email={suggestedAccount.email}
        header={
          <UnauthorizedHeader>
            <UnauthorizedComponent
              applicationDestination={applicationDestination}
              emailAddress={session.email}
              suggested={true}
            />
          </UnauthorizedHeader>
        }>
        <ButtonContainer>
          <TallButton
            appearance="primary"
            onClick={
              handleSessionClick(suggestedAccount, {
                ...eventAttributes,
                accountIndex: index,
              }) || undefined
            }
            isFullWidth
            id="continue">
            <FormattedMessage id="select.account.continue" defaultMessage="Continue" />
          </TallButton>
        </ButtonContainer>
      </Account>
    ) : (
      <>
        <Account
          id={`account-${session.accountId}`}
          key={`session-${session.sessionId}`}
          primary={true}
          avatarUrl={session.avatarUrl}
          displayName={session.displayName}
          email={session.email}>
          <UnauthorizedWrapper>
            <UnauthorizedComponent
              applicationDestination={applicationDestination}
              emailAddress={session.email}
              suggested={false}
            />
          </UnauthorizedWrapper>
        </Account>
        {sessions.length === 1 && (
          <ButtonWrapper>
            <TallButton
              id="navigate-to-login-prompt"
              appearance="primary"
              isFullWidth
              onClick={navigateToLoginPrompt}>
              <FormattedMessage
                id="select.account.add"
                defaultMessage="Log in to another account"
              />
            </TallButton>
          </ButtonWrapper>
        )}
      </>
    );
  };

  interface LoginHintProps {
    session: Session;
    index: number;
    suggestedAccount: Session | undefined;
    handleOnClick: ((_: React.MouseEvent<HTMLElement>) => void) | null;
  }

  const LoginHint = ({ session, index, suggestedAccount, handleOnClick }: LoginHintProps) => {
    if (!session.hint) {
      return null;
    }
    switch (session.hint) {
      case LoginHintType.EmailAddress:
        if (handleOnClick) {
          return (
            <Account
              id={`account-${session.accountId}`}
              key={`session-${session.sessionId}`}
              primary={true}
              avatarUrl={session.avatarUrl}
              displayName={session.displayName}
              email={session.email}>
              <ButtonContainer>
                <TallButton appearance="primary" onClick={handleOnClick} isFullWidth id="continue">
                  <FormattedMessage id="select.account.continue" defaultMessage="Continue" />
                </TallButton>
              </ButtonContainer>
            </Account>
          );
        }
        break;
      case LoginHintType.NotEmailAddress:
        return (
          <LoginHintNotEmailAddress
            index={index}
            suggestedAccount={suggestedAccount}
            session={session}
          />
        );
      default:
        return null;
    }
    return null;
  };

  const renderAccounts = () => {
    const suggestedAccount = getSuggestedAccount();

    const accountsList = sessions.map((session, index) => {
      const { accountId, displayName, email } = session;

      const handleOnClick = handleSessionClick(session, {
        ...eventAttributes,
        accountIndex: index,
      });

      return session.hint ? (
        <LoginHint
          key="session-hint"
          session={session}
          index={index}
          suggestedAccount={suggestedAccount}
          handleOnClick={handleOnClick}
        />
      ) : (
        !session.suggested && (
          <AccountsListItem key={`session-${session.sessionId}`}>
            <Account
              id={`account-${accountId}`}
              onClick={handleOnClick || undefined}
              avatarUrl={session.avatarUrl}
              displayName={displayName}
              email={email}
            />
          </AccountsListItem>
        )
      );
    });

    const hasMultipleSessions = sessions.length > 1;
    const noLoginHint = sessions.length >= 1 && sessions[0].hint !== LoginHintType.NotEmailAddress;
    const canAddSessions =
      (hasMultipleSessions || noLoginHint) && sessions.length <= MAXIMUM_ASSOCIATED_SESSIONS;
    if (canAddSessions) {
      accountsList.push(
        <AccountsListItem key="navigate-to-login-prompt">
          <AccountContainer id="navigate-to-login-prompt" onClick={navigateToLoginPrompt}>
            <RowButtonContainer>
              <AvatarContainer>
                <UserAvatarCircleIcon label="" size="medium" />
              </AvatarContainer>
              <TextContainer>
                <Paragraph>
                  <FormattedMessage
                    id="select.account.another"
                    defaultMessage="Add another account"
                  />
                </Paragraph>
              </TextContainer>
            </RowButtonContainer>
          </AccountContainer>
        </AccountsListItem>
      );
    }
    if (hasMultipleSessions) {
      const showRequestAccessLink = shouldShowRequestAccessLink();
      const showMaximumSessionsText = sessions.length > MAXIMUM_ASSOCIATED_SESSIONS;
      const noBorder = !showMaximumSessionsText && !showRequestAccessLink;

      accountsList.push(
        <AccountsListItem key="navigate-to-remove-account-prompt">
          <AccountContainer
            id="navigate-to-remove-account-prompt"
            noBorder={noBorder}
            onClick={navigateToRemoveAccountPrompt}>
            <RowButtonContainer>
              <AvatarContainer>
                <CrossCircleIcon label="" size="medium" />
              </AvatarContainer>
              <TextContainer>
                <Paragraph>
                  <FormattedMessage
                    id="select.account.footer.navigate.to.remove.account"
                    defaultMessage="Remove accounts from this browser"
                  />
                </Paragraph>
              </TextContainer>
            </RowButtonContainer>
          </AccountContainer>
        </AccountsListItem>
      );
    }

    const loginHintAccountIndex = sessions.findIndex(session => session.hint);
    const loginHintAccount =
      loginHintAccountIndex >= 0 ? accountsList.splice(loginHintAccountIndex, 1) : [];
    const accounts = loginHintAccount.concat(<AccountsList>{accountsList}</AccountsList>);

    return accounts;
  };

  const getSuggestedAccount = () => sessions.find(session => session.suggested);

  if (sessions.length > 0) {
    return (
      <WhiteboxPage
        pageId={pageId}
        header={renderHeader()}
        whiteboxFooter={renderMainContentFooter()}
        isLoading={isLoading}>
        <Accounts>{renderAccounts()}</Accounts>
        {sessions.length > MAXIMUM_ASSOCIATED_SESSIONS && (
          <SectionMessageWrapper>
            <SectionMessage testId="selectaccountmaximumassociatedsessions" appearance="warning">
              <FormattedMessage
                id="select.account.maximum.associated.sessions"
                defaultMessage="You can only add up to 10 accounts. Remove at least one account from this browser to add another."
              />
            </SectionMessage>
          </SectionMessageWrapper>
        )}
      </WhiteboxPage>
    );
  }

  navigateToLoginPrompt();
  return null;
};

export default SelectAccountPage;
